"use client";

import { ArrowUpRightIcon, FileTextIcon, XIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import { cn } from "@/lib/utils";
import { AnalysisTargetTypeIcon } from "./analysis-target-options";
import {
  buildAnalysisTargetHref,
  isSameAnalysisTargetFallbackId,
} from "./analysis-target-path";

function getProjectTypeLabel(
  projectType: AnalysisTarget["projectType"],
): string {
  if (projectType === "IndexWorkerProject") {
    return "Index Worker";
  }
  if (projectType === "SearchAnalyticsProject") {
    return "Search Analytics";
  }
  return "User Analytics";
}

function isResolvedName(
  id: string | undefined,
  name: string | undefined,
): name is string {
  return Boolean(
    name && id && !isSameAnalysisTargetFallbackId(name.trim(), id),
  );
}

/**
 * project / detail が URL fallback（name === id）でも、表示層では生 ID を出さない。
 *
 * 契約:
 * - 解決済みラベルがあるときだけ `project / detail` を表示する
 * - project 名が未解決の間は project type 名で置き換える
 * - どの経路でも `projectId/viewId/iwId` を UI 表示へ流さない
 */
function getAnalysisTargetDisplayProjectLabel(target: AnalysisTarget): string {
  if (isResolvedName(target.projectId, target.projectName)) {
    return target.projectName;
  }
  return getProjectTypeLabel(target.projectType);
}

function getAnalysisTargetResolvedDetailName(
  target: AnalysisTarget,
): string | null {
  if (isResolvedName(target.iwId, target.iwName)) {
    return target.iwName;
  }
  if (isResolvedName(target.viewId, target.viewName)) {
    return target.viewName;
  }
  return null;
}

function getAnalysisTargetDisplayLabel(target: AnalysisTarget): string {
  const projectLabel = getAnalysisTargetDisplayProjectLabel(target);
  const detailLabel = getAnalysisTargetResolvedDetailName(target);
  if (detailLabel) {
    return `${projectLabel} / ${detailLabel}`;
  }
  return projectLabel;
}

function getAnalysisTargetDisplayTitle(target: AnalysisTarget): string {
  return (
    getAnalysisTargetResolvedDetailName(target) ??
    getAnalysisTargetDisplayProjectLabel(target)
  );
}

type AnalysisTargetMentionTextProps = {
  target: AnalysisTarget;
  className?: string;
  labelClassName?: string;
  underlined?: boolean;
  iconLayout?: "compensated" | "fixed";
  iconVariant?: "link-document" | "target-type";
};

/**
 * 入力欄内メンションで使う、`@` 置き換え済みの表示テキスト。
 */
export function AnalysisTargetMentionText({
  target,
  className,
  labelClassName,
  underlined = true,
  iconLayout = "compensated",
  iconVariant = "link-document",
}: AnalysisTargetMentionTextProps) {
  const mentionLabel = getAnalysisTargetDisplayLabel(target);
  const isCompensatedLayout = iconLayout === "compensated";

  return (
    <span
      className={cn(
        "relative py-0 align-baseline text-foreground/95 [font-size:inherit] [line-height:inherit]",
        isCompensatedLayout ? "inline" : "inline-flex items-center",
        className,
      )}
    >
      {/*
       * カーソル位置をズラさないため、フロー上は実文字列 `@` の幅を維持し、
       * 見た目だけ絶対配置アイコンで置き換える。
       */}
      {iconLayout === "compensated" ? (
        <span
          aria-hidden
          className="relative z-10 inline-block select-none text-transparent align-baseline"
        >
          @
          <span className="pointer-events-none absolute inset-0 inline-flex items-center justify-center">
            <span className="relative inline-flex size-[1.1em] -translate-x-[0.24em] items-center justify-center">
              <FileTextIcon className="size-[0.92em] text-foreground/85" />
              <ArrowUpRightIcon className="-bottom-[0.12em] -right-[0.18em] absolute size-[0.56em] rounded-full bg-background p-[0.03em] text-foreground/75 transition-colors group-hover:bg-accent" />
            </span>
          </span>
        </span>
      ) : (
        <span
          aria-hidden
          className="relative z-10 mr-[0.34em] inline-flex h-[1.18em] w-[1.18em] shrink-0 items-center justify-center leading-none"
        >
          {iconVariant === "target-type" ? (
            <AnalysisTargetTypeIcon
              projectType={target.projectType}
              className="size-[0.92em] text-muted-foreground"
            />
          ) : (
            <>
              <FileTextIcon className="size-[0.94em] text-foreground/85" />
              <ArrowUpRightIcon className="-bottom-[0.09em] -right-[0.14em] absolute size-[0.56em] rounded-full bg-background p-[0.03em] text-foreground/75 transition-colors group-hover:bg-accent" />
            </>
          )}
        </span>
      )}
      <span
        className={cn(
          "relative z-10 font-normal [line-height:inherit]",
          underlined &&
            "underline decoration-[0.05em] decoration-muted-foreground/80 underline-offset-[0.12em]",
          labelClassName,
        )}
      >
        {mentionLabel}
      </span>
    </span>
  );
}

function getAnalysisTargetDescription(target: AnalysisTarget): string {
  const projectLabel = getAnalysisTargetDisplayProjectLabel(target);
  if (getAnalysisTargetResolvedDetailName(target)) {
    return projectLabel;
  }
  return projectLabel;
}

type AnalysisTargetInlineMentionProps = {
  target: AnalysisTarget;
  metadata?: AnalysisTargetInlineMentionMetadata;
};

export type AnalysisTargetInlineMentionMetadata = {
  projectTypeLabel: string;
  detail: string | null;
};

/**
 * 入力オーバーレイ内で使うメンショントークン表示。
 */
export function AnalysisTargetInlineMention({
  target,
  metadata,
}: AnalysisTargetInlineMentionProps) {
  const pathname = usePathname();
  /**
   * App Router の `params.workspaceId` は既に URL エンコード済みの場合があり、
   * その値を再度 `encodeURIComponent` すると `%25` 化して二重エンコードになる。
   * ここでは pathname 由来に統一し、`buildAnalysisTargetHref` 側の decode -> encode 1 回で正規化する。
   */
  const href = buildAnalysisTargetHref(target, pathname);
  const canNavigate = href !== null;
  const projectTypeLabel =
    metadata?.projectTypeLabel ?? getProjectTypeLabel(target.projectType);
  const title = getAnalysisTargetDisplayTitle(target);
  const description =
    metadata === undefined
      ? getAnalysisTargetDescription(target)
      : metadata.detail;

  const linkToken = (
    <a
      href={href ?? "#"}
      target={canNavigate ? "_blank" : undefined}
      rel={canNavigate ? "noopener noreferrer" : undefined}
      role="link"
      tabIndex={-1}
      contentEditable={false}
      className={cn(
        "group pointer-events-auto cursor-pointer select-none rounded-[3px] align-baseline text-inherit no-underline [line-height:inherit] transition-[background-color,box-shadow] duration-100 ease-out box-decoration-clone",
        "hover:bg-accent hover:ring-4 hover:ring-accent",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
      onMouseDown={(event) => {
        // textarea のフォーカスを維持し、入力継続中のキャレットを保持する。
        event.preventDefault();
      }}
      onClick={(event) => {
        if (!canNavigate) {
          event.preventDefault();
        }
      }}
    >
      <AnalysisTargetMentionText
        target={target}
        underlined
        iconLayout="compensated"
        iconVariant="link-document"
      />
    </a>
  );

  return (
    <HoverCard openDelay={120}>
      <HoverCardTrigger asChild>{linkToken}</HoverCardTrigger>
      <HoverCardContent
        className="w-[320px] p-0"
        align="start"
        side="top"
        sideOffset={10}
      >
        <div className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-muted">
              <AnalysisTargetTypeIcon
                projectType={target.projectType}
                className="size-3.5 text-muted-foreground"
              />
            </span>
            <span className="inline-flex h-5 items-center rounded-sm border border-border/70 bg-muted/30 px-1.5 font-medium text-[11px] leading-none text-muted-foreground">
              {projectTypeLabel}
            </span>
          </div>
          <div className="line-clamp-1 font-semibold text-sm leading-5">
            {title}
          </div>
          {description ? (
            <div className="line-clamp-3 break-words text-muted-foreground text-xs leading-5">
              {description}
            </div>
          ) : null}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

type AnalysisTargetSelectedBadgeProps = {
  target: AnalysisTarget;
  onRemove?: () => void;
  removable?: boolean;
};

/**
 * 入力欄上部で選択済みコンテキストを表示するバッジ。
 */
export function AnalysisTargetSelectedBadge({
  target,
  onRemove,
  removable = true,
}: AnalysisTargetSelectedBadgeProps) {
  const showRemoveButton = removable && onRemove;
  const fullLabel = getAnalysisTargetDisplayLabel(target);

  return (
    <span
      className="group relative inline-flex h-6 max-w-[280px] shrink-0 items-center rounded-full border bg-background px-1 text-sm sm:max-w-[360px]"
      // 省略表示時でも全文を確認できるよう、ブラウザ標準 tooltip を付与する。
      title={fullLabel}
    >
      <span className="inline-flex min-w-0 items-center rounded-sm text-muted-foreground/85">
        <AnalysisTargetMentionText
          target={target}
          underlined={false}
          iconLayout="fixed"
          iconVariant="target-type"
          className="min-w-0 max-w-full pl-[1px] pr-0.5 text-muted-foreground/85 text-sm"
          labelClassName="inline-block max-w-full truncate leading-5"
        />
      </span>
      {showRemoveButton ? (
        <>
          {/* Notion と同様に、hover時のみ右側フェードを重ねて×ボタン領域の視認性を担保する。 */}
          <span className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 rounded-r-full bg-gradient-to-r from-transparent via-background/80 to-background opacity-0 group-hover:opacity-100 group-focus-within:opacity-100" />
          <span className="absolute inset-y-0 right-1 z-20 inline-flex items-center justify-center">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-5 rounded-full text-muted-foreground opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto hover:text-foreground"
              onClick={onRemove}
              aria-label="Remove context"
            >
              <XIcon className="size-3" />
            </Button>
          </span>
        </>
      ) : null}
    </span>
  );
}
